import useSafeState from "../utils/safe-state";
import {Button, Col, DatePicker, Form, InputNumber, Row, Select, Spin} from "antd";
import React, {useEffect} from "react";
import {Trans} from "@lingui/macro";
import {useAxios} from "../utils/hooks";
import moment from "moment";
import {openNotification} from "../utils/openNotification";

interface Interface{
    data?: any;
    mode?: 'Edit' | 'Add new';
    onClose?: () => void;
}

const LocationAssignmentRuleDrawer = (props: Interface) => {

    const axiosInstance = useAxios(process.env.REACT_APP_API_BASE_URL + "");
    const data = props.data || {};
    const [form] = Form.useForm();
    const [loading, setLoading] = useSafeState(false);
    const [itemOptions, setItemOptions] = useSafeState<any[]>([]);
    const [locationOptions, setLocationOptions] = useSafeState<any[]>([]);
    const [itemLoading, setItemLoading] = useSafeState(false);

    useEffect(() => {
        form.resetFields();
        getItems();
        getLocations();

        if(props.mode === 'Edit') {
            const fieldsValues: any[] = [];
            fieldsValues.push({name: 'id', value: data.id});
            fieldsValues.push({name: 'itemId', value: data.item ? data.item.id : null});
            fieldsValues.push({name: 'locationId', value: data.location ? data.location.id : null});
            fieldsValues.push({name: 'quantity', value: data.quantity});
            fieldsValues.push({name: 'validFrom', value: data.validFrom ? moment(data.validFrom) : null});
            fieldsValues.push({name: 'validTo', value: data.validTo ? moment(data.validTo) : null});
            fieldsValues.push({name: 'version', value: data.version});
            form.setFields(fieldsValues);
        }
    }, []);

    const getItems = () => {
        if (axiosInstance.current != null) {
            setItemLoading(true);
            axiosInstance.current
                .get('/resource/items')
                .then(response => {
                    const items = Array.isArray(response.data) ? response.data : [];
                    setItemOptions(items);
                })
                .catch(function (error: any) {
                    console.log(error);
                })
                .finally(() => {
                    setItemLoading(false);
                });
        }
    };

    const getLocations = () => {
        if (axiosInstance.current != null) {
            axiosInstance.current
                .get('/resource/locations')
                .then(response => {
                    const locations = Array.isArray(response.data) ? response.data : [];
                    setLocationOptions(locations);
                })
                .catch(function (error: any) {
                    console.log(error);
                });
        }
    };

    const handleEdit = () => {
        if (axiosInstance.current != null) {
            setLoading(true);

            axiosInstance.current
                .put(
                    '/resource/location-assignment-rules/' + encodeURIComponent(form.getFieldValue(['id'])) + '/from-dto',
                    {
                        id: form.getFieldValue(['id']),
                        location: { id: form.getFieldValue(['locationId']) },
                        item: { id: form.getFieldValue(['itemId']) },
                        quantity: form.getFieldValue(['quantity']) || null,
                        validFrom: form.getFieldValue(['validFrom']) ? moment(form.getFieldValue(['validFrom'])).format('YYYY-MM-DD HH:mm') : null,
                        validTo: form.getFieldValue(['validTo']) ? moment(form.getFieldValue(['validTo'])).format('YYYY-MM-DD HH:mm') : null,
                        version: form.getFieldValue(['version'])
                    }
                )
                .then(() => {
                    openNotification('customSuccess', 'Successfully updated.');
                    if (props.onClose) props.onClose();
                })
                .catch(function (error: any) {
                    openNotification('error', error, props);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    };

    const handleFinish = () => {
        if (axiosInstance.current != null) {
            setLoading(true);

            axiosInstance.current
                .post(
                    '/resource/location-assignment-rules/from-dto',
                    {
                        location: { id: form.getFieldValue(['locationId']) },
                        item: { id: form.getFieldValue(['itemId']) },
                        quantity: form.getFieldValue(['quantity']) || null,
                        validFrom: form.getFieldValue(['validFrom']) ? moment(form.getFieldValue(['validFrom'])).format('YYYY-MM-DD HH:mm') : null,
                        validTo: form.getFieldValue(['validTo']) ? moment(form.getFieldValue(['validTo'])).format('YYYY-MM-DD HH:mm') : null
                    }
                )
                .then(() => {
                    openNotification('customSuccess', 'Successfully created.');
                    if (props.onClose) props.onClose();
                })
                .catch(function (error: any) {
                    openNotification('error', error, props);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }

    const onReset = () => {
        form.resetFields();
    };

    return (
        <Form
            id="newLocationAssignmentRule"
            layout='vertical'
            form={form}
            onFinish={() => (props.mode === 'Edit' ? handleEdit() : handleFinish())}
        >
            <Spin spinning={loading || itemLoading}>
                <Row gutter={24}>
                    <Col span={12}>
                        <Form.Item
                            name="locationId"
                            label={<Trans>Location</Trans>}
                            rules={[{ required: true, message: <Trans>Please select the location</Trans> }]}
                        >
                            <Select
                                showSearch
                                allowClear
                                placeholder={<Trans>Select location</Trans>}
                                optionFilterProp="children"
                            >
                                {locationOptions.map(loc => (
                                    <Select.Option key={loc.id} value={loc.id}>
                                        {loc.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="itemId"
                            label={<Trans>Item</Trans>}
                            rules={[{ required: true, message: <Trans>Please select the item</Trans> }]}
                        >
                            <Select
                                showSearch
                                allowClear
                                placeholder={<Trans>Select item</Trans>}
                                optionFilterProp="children"
                            >
                                {itemOptions.map(item => (
                                    <Select.Option key={item.id} value={item.id}>
                                        {item.itemNo} - {item.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={24}>
                    <Col span={12}>
                        <Form.Item
                            name="quantity"
                            label={<Trans>Quantity</Trans>}
                        >
                            <InputNumber min={1} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={24}>
                    <Col span={12}>
                        <Form.Item
                            name="validFrom"
                            label={<Trans>Valid From</Trans>}
                        >
                            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="validTo"
                            label={<Trans>Valid To</Trans>}
                        >
                            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                        </Form.Item>
                    </Col>
                </Row>
            </Spin>

            <div style={{ marginTop: 12 }}>
                <Button htmlType="button" style={{ marginRight: 8 }} onClick={() => onReset()}>
                    <Trans>Clear</Trans>
                </Button>
                <Button
                    loading={loading}
                    htmlType="submit"
                    type="primary"
                >
                    <Trans>Save</Trans>
                </Button>
            </div>
        </Form>
    );

}
export default LocationAssignmentRuleDrawer;
